import {
  Children,
  cloneElement,
  isValidElement,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
} from 'react';
import clsx from 'clsx';
import { useEmVista } from '../hooks/use-em-vista';

/** De onde o elemento vem. `parado` é fade puro, sem deslocamento. */
export type Direcao =
  'baixo' | 'cima' | 'esquerda' | 'direita' | 'parado' | 'profundidade';

/** Quanto ele percorre. Ver `--deslocamento-*` em `estilos/motion.css`. */
export type Peso = 'leve' | 'medio' | 'forte';

const CLASSE_DIRECAO: Record<Direcao, string> = {
  baixo: 'entra-de-baixo',
  cima: 'entra-de-cima',
  esquerda: 'entra-da-esquerda',
  direita: 'entra-da-direita',
  parado: 'entra-parado',
  profundidade: 'entra-em-profundidade',
};

const TOKEN_PESO: Record<Peso, string> = {
  leve: 'var(--deslocamento-leve)',
  medio: 'var(--deslocamento-medio)',
  forte: 'var(--deslocamento-forte)',
};

type RevealProps = {
  children: ReactNode;
  className?: string;
  /** Atraso em ms, tipicamente preenchido pelo `RevealGrupo` (stagger). */
  atraso?: number;
  direcao?: Direcao;
  peso?: Peso;
  /** Fração do elemento visível para disparar. Menor = dispara mais cedo. */
  limiar?: number;
  /**
   * Some de novo ao sair da tela e reanima ao voltar. Ligado por padrão.
   *
   * É o que mantém a página viva na segunda passagem, em vez de virar
   * documento estático depois que tudo apareceu uma vez.
   */
  repetir?: boolean;
};

/**
 * Revela o filho quando ele entra no viewport.
 *
 * ## O que mudou, e por quê
 * Antes havia UM gesto para a página inteira: `opacity` + `translateY(16px)`,
 * 500 ms, para tudo — da manchete ao rodapé. É o que faz uma página com 59
 * animações parecer sem efeito nenhum: nada tem peso próprio, e 16px numa tela
 * de 812px é uma distância que o olho não registra como movimento.
 *
 * Agora a direção e o PESO são escolhidos por quem chama: uma manchete entra
 * `forte`, um item de lista entra `leve`. Os valores em si vivem em
 * `estilos/motion.css` — aqui se escolhe entre eles, nunca um número solto.
 *
 * ## Por que `data-visivel`, e não troca de classe
 * O estado inicial e a transição são do CSS; o React só marca o atributo. A
 * animação inteira fica legível num arquivo só, e o componente vira o que ele
 * de fato é: um observador de viewport.
 *
 * ## Nunca esconde conteúdo
 * Sem `IntersectionObserver`, ou com `prefers-reduced-motion`, `useEmVista` já
 * nasce visível. E mesmo invisível, o texto está no DOM desde o primeiro byte
 * — o que importa para busca e para leitor de tela.
 */
export function Reveal({
  children,
  className,
  atraso = 0,
  direcao = 'baixo',
  peso = 'medio',
  limiar = 0.15,
  repetir = true,
}: RevealProps): JSX.Element {
  const { ref, emVista } = useEmVista<HTMLDivElement>(limiar, repetir);

  const estilo = {
    '--atraso': `${String(atraso)}ms`,
    '--deslocamento': TOKEN_PESO[peso],
  } as CSSProperties;

  return (
    <div
      ref={ref}
      data-visivel={emVista ? 'true' : 'false'}
      className={clsx('entra', CLASSE_DIRECAO[direcao], className)}
      style={estilo}
    >
      {children}
    </div>
  );
}

type RevealGrupoProps = {
  children: ReactNode;
  className?: string;
  /** Intervalo entre a revelação de cada filho direto, em ms. */
  passoMs?: number;
  /** Repassados a cada filho. */
  direcao?: Direcao;
  peso?: Peso;
};

/**
 * Aplica `Reveal` a cada filho direto com atraso crescente.
 *
 * O passo padrão saiu de 60 para 90 ms: 60 era rápido demais para o olho ler
 * como sequência — os cartões chegavam quase juntos, e a cascata que justifica
 * o stagger não acontecia.
 *
 * O atraso é LIMITADO a 6 passos. Numa grade de doze cartões, o último
 * esperaria mais de um segundo depois do primeiro, e quem rola rápido o veria
 * aparecer sozinho, muito depois — o que se lê como travamento, não como
 * elegância.
 */
export function RevealGrupo({
  children,
  className,
  passoMs = 90,
  direcao,
  peso,
}: RevealGrupoProps): JSX.Element {
  const filhos = Children.toArray(children);
  return (
    <div className={className}>
      {filhos.map((filho, indice) =>
        isValidElement(filho) ? (
          cloneElement(filho as ReactElement<Partial<RevealProps>>, {
            key: filho.key ?? indice,
            atraso: Math.min(indice, 6) * passoMs,
            ...(direcao ? { direcao } : {}),
            ...(peso ? { peso } : {}),
          })
        ) : (
          <span key={indice}>{filho}</span>
        ),
      )}
    </div>
  );
}
